import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  alpha,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Inventory as InventoryIcon,
  Numbers as NumbersIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as TimeIcon,
  Assignment as BorrowIcon,
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageInventory, canDeleteStock } from '../../../utils/inventoryPermissions';
import dayjs from 'dayjs';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import {
  injectDetailsPageStyles,
} from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';

// ── Design tokens ────────────────────────────────────────────────────────────
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const SURFACE_TOP  = '#152640';

const TEXT_BODY  = '#E2E8F0';
const TEXT_MUTED = '#64748B';

// ── Shared sub-components ────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children, sx = {}, bodyFlex = false }) => (
  <Box sx={{
    borderRadius: '14px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
    ...sx,
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.08)}`,
      background: alpha(accent, 0.03),
      flexShrink: 0,
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.72rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2, ...(bodyFlex && { flex: 1, display: 'flex', flexDirection: 'column' }) }}>
      {children}
    </Box>
  </Box>
);

const InfoRow = ({ icon: Icon, label, value, color = NEON, emptyLabel = 'Not specified' }) => {
  const isEmpty = !value;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1, borderRadius: '9px',
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.62rem', color: TEXT_MUTED,
          fontFamily: "'DM Sans', sans-serif",
          textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.86rem',
          color: isEmpty ? TEXT_MUTED : TEXT_BODY,
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {value || emptyLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const MetricCard = ({ label, value, icon: Icon, color = NEON, sub, subColor, onClick }) => (
  <Box
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    sx={{
      position: 'relative', borderRadius: '14px', px: 2.5, py: 2,
      width: '100%', textAlign: 'left', border: 'none',
      background: `linear-gradient(145deg, ${alpha(color, 0.08)} 0%, ${alpha(SURFACE_UP, 0.55)} 100%)`,
      outline: `1px solid ${alpha(color, 0.18)}`,
      boxShadow: `inset 0 1px 0 ${alpha(color, 0.08)}`,
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'outline-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
      ...(onClick && {
        '&:hover': {
          outlineColor: alpha(color, 0.4),
          boxShadow: `0 0 20px ${alpha(color, 0.13)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
          transform: 'translateY(-2px)',
        },
      }),
    }}
  >
    {/* Ambient glow orb */}
    <Box sx={{
      position: 'absolute', top: -18, right: -18,
      width: 66, height: 66, borderRadius: '50%',
      background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none',
    }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.9 }}>
      {Icon && <Icon sx={{ fontSize: 12, color: alpha(color, 0.7) }} />}
      <Typography sx={{
        fontSize: '0.62rem', fontFamily: "'DM Sans', sans-serif",
        color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600,
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontSize: '1.55rem', fontWeight: 700,
      fontFamily: "'Rajdhani', sans-serif",
      color, letterSpacing: 0.3, lineHeight: 1.15,
    }}>
      {value ?? '—'}
    </Typography>
    {sub && (
      <Typography sx={{
        fontSize: '0.68rem', color: subColor || TEXT_MUTED,
        fontFamily: "'DM Sans', sans-serif", mt: 0.4,
      }}>
        {sub}
      </Typography>
    )}
    {onClick && (
      <ChevronRightIcon sx={{
        position: 'absolute', bottom: 10, right: 10,
        fontSize: 14, color: alpha(color, 0.35),
      }} />
    )}
  </Box>
);

// ── Action Buttons ────────────────────────────────────────────────────────────
const ActionButtons = ({ canManageStock, canDelete, onEdit, onDelete }) => {
  if (!canManageStock) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
      {canDelete && <Button
        startIcon={<EditIcon sx={{ fontSize: 15 }} />}
        onClick={onEdit}
        sx={{
          borderRadius: '10px', px: 2, py: 0.65,
          fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.82rem',
          background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
          color: NEON, transition: 'all 0.18s ease',
          '&:hover': { background: alpha(NEON, 0.07), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.18)}` },
        }}
      >
        Edit
      </Button>}
      <Button
          startIcon={<ArchiveIcon sx={{ fontSize: 15 }} />}
          onClick={onDelete}
          sx={{
            borderRadius: '10px', px: 2, py: 0.65,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.82rem',
            background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.55)}`,
            color: '#F87171', transition: 'all 0.18s ease',
            '&:hover': { background: alpha('#F87171', 0.08), borderColor: '#F87171', boxShadow: `0 0 12px ${alpha('#F87171', 0.18)}` },
          }}
        >
          Archive
      </Button>
    </Box>
  );
};

// ── Borrow Status Badge ───────────────────────────────────────────────────────
const BorrowBadge = ({ status, penaltyDate, archivedAt }) => {
  const isOverdue = !archivedAt && status !== 'returned' && new Date(penaltyDate) < new Date();

  const cfg = archivedAt
    ? { label: 'Archived', bg: alpha('#94A3B8', 0.1), color: '#94A3B8', border: alpha('#94A3B8', 0.25) }
    : isOverdue
    ? { label: 'Overdue', bg: alpha('#F87171', 0.1), color: '#F87171', border: alpha('#F87171', 0.25) }
    : status === 'approved'
    ? { label: 'Approved', bg: alpha(NEON, 0.09), color: NEON, border: alpha(NEON, 0.22) }
    : status === 'pending'
    ? { label: 'Pending', bg: alpha('#FCD34D', 0.1), color: '#FCD34D', border: alpha('#FCD34D', 0.25) }
    : { label: 'Returned', bg: alpha(NEON3, 0.09), color: NEON3, border: alpha(NEON3, 0.22) };

  return (
    <Box sx={{
      px: 1, py: 0.35, borderRadius: '6px',
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      fontFamily: "'DM Sans', sans-serif", fontSize: '0.63rem',
      fontWeight: 700, color: cfg.color, letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const StockDetails = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [stock,           setStock]           = useState(null);
  const [department,      setDepartment]      = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [successMessage,  setSuccessMessage]  = useState(null);
  const [deleteDialogOpen,setDeleteDialogOpen]= useState(false);

  const canManageStock  = canManageInventory(currentUser);
  const formatAuditUser = (u) => (u?.name ? String(u.name) : null);

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!canManageStock) { navigate('/dashboard'); return; }
    fetchStock();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, id, canManageStock, navigate]);

  const fetchStock = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/stocks/${id}`);
      setStock(res.data);
      if (res.data.department_id) {
        const dRes = await api.get(`/departments/${res.data.department_id}`);
        setDepartment(dRes.data);
      }
      setError(null);
    } catch {
      setError('Failed to load stock details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStock = async () => {
    try {
      const stockName = stock?.things || 'Inventory Item';
      await api.request({ method: 'post', url: `/stocks/${id}/archive` });
      await fetchStock();
      setDeleteDialogOpen(false);
      setSuccessMessage(`Inventory item "${stockName}" has been archived.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to archive stock item. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  const getQuantityColor = (qty) => {
    if (qty === 0)   return '#F87171';
    if (qty <= 5)    return '#FCD34D';
    if (qty <= 20)   return NEON2;
    return NEON3;
  };

  const getQuantityLabel = (qty) => {
    if (qty === 0)   return 'Out of Inventory';
    if (qty <= 5)    return 'Low Inventory';
    if (qty <= 20)   return 'Medium Inventory';
    return 'Available';
  };

  // ── Guard renders ────────────────────────────────────────────────────────
  if (!currentUser) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message="Please log in to view this page." severity="error" />
    </Container>
  );
  if (!canManageStock) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message="You don't have permission to view this page." severity="error" />
    </Container>
  );
  if (loading) return <Loading message="Loading stock details" size={56} variant="primary" />;
  if (error && !stock) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message={error} severity="error" />
    </Container>
  );
  if (!stock) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message="Inventory item not found" severity="error" />
    </Container>
  );

  // ── Derived values ────────────────────────────────────────────────────────
  const borrowRecords = Array.isArray(stock.borrows) ? stock.borrows : [];
  const currentlyBorrowed = Number(
    stock.currently_borrowed ??
      borrowRecords
        .filter((b) => b.status === 'pending' || b.status === 'approved')
        .reduce((t, b) => t + (Number(b.quantity) || 0), 0)
  );
  const availableQuantity =
    stock.available_quantity != null
      ? Number(stock.available_quantity)
      : Math.max(0, Number(stock.quantity) - currentlyBorrowed);
  const activeBorrows   = borrowRecords.filter((b) => b.status !== 'returned');
  const overdueBorrows  = borrowRecords.filter((b) => b.status !== 'returned' && new Date(b.penalty_date) < new Date());
  const availabilityPct = stock.quantity > 0 ? Math.min((availableQuantity / stock.quantity) * 100, 100) : 0;
  const quantityColor   = getQuantityColor(availableQuantity);

  const goToBorrowList = () =>
    navigate('/borrows', { state: { filterStockId: id, stockLabel: stock.things } });

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      {/* ── Page Header ── */}
      <Header
        title="Inventory Details"
        subtitle="View stock information"
        icon={InventoryIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          <ActionButtons
            canManageStock={canManageStock}
            canDelete={canDeleteStock(currentUser, stock)}
            onEdit={() => navigate(`/stocks/edit/${id}`)}
            onDelete={() => setDeleteDialogOpen(true)}
          />
        }
      />

      <FeedbackMessage message={error}          severity="error"   onClose={() => setError(null)}          sx={{ mb: 2 }} />
      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── Identity Hero Card ── */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          {/* Name + status row */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-start',
            justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 50, height: 50, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.22)}, ${alpha(NEON, 0.12)})`,
                border: `1.5px solid ${alpha(NEON2, 0.32)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <InventoryIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontSize: '1.4rem', fontWeight: 700,
                  fontFamily: "'Rajdhani', sans-serif",
                  color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15,
                }}>
                  {stock.things}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  {stock.place || 'No storage location'}
                  {department?.name ? ` · ${department.name}` : ''}
                </Typography>
              </Box>
            </Box>

            {/* Status pill */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.55, borderRadius: '20px',
              background: alpha(quantityColor, 0.1),
              border: `1px solid ${alpha(quantityColor, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: quantityColor }} />
              <Typography sx={{
                fontSize: '0.73rem', fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700, color: quantityColor, letterSpacing: '0.05em',
              }}>
                {getQuantityLabel(availableQuantity)}
              </Typography>
            </Box>
          </Box>

          {/* ── Metric Cards ── */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}>
            <MetricCard
              label="Total Quantity" value={stock.quantity}
              icon={NumbersIcon} color={TEXT_BODY}
            />
            <MetricCard
              label="Available" value={availableQuantity}
              icon={TrendingUpIcon} color={quantityColor}
              sub="Ready to borrow"
            />
            <MetricCard
              label="Borrowed" value={currentlyBorrowed}
              icon={TrendingDownIcon}
              color={currentlyBorrowed > 0 ? NEON2 : TEXT_MUTED}
              sub="Currently out"
              onClick={goToBorrowList}
            />
            <MetricCard
              label="Active Borrows" value={activeBorrows.length}
              icon={TimeIcon}
              color={activeBorrows.length > 0 ? NEON2 : TEXT_MUTED}
              sub={overdueBorrows.length > 0 ? `${overdueBorrows.length} overdue` : 'All on time'}
              subColor={overdueBorrows.length > 0 ? '#F87171' : TEXT_MUTED}
              onClick={goToBorrowList}
            />
          </Box>

          {/* ── Availability Bar ── */}
          <Box sx={{ mt: 2, pt: 1.8, borderTop: `1px solid ${alpha(NEON, 0.1)}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: TEXT_MUTED }}>
                Availability
              </Typography>
              <Typography sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.68rem', color: alpha(NEON, 0.6),
              }}>
                {Math.round(availabilityPct)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={availabilityPct}
              sx={{
                height: 9, borderRadius: 5,
                background: alpha(SURFACE_UP, 0.7),
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${quantityColor}, ${alpha(quantityColor, 0.6)})`,
                  boxShadow: `0 0 10px ${alpha(quantityColor, 0.38)}`,
                  borderRadius: 5,
                },
              }}
            />
            <Typography sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.65), mt: 0.75,
            }}>
              {availableQuantity} of {stock.quantity} units — {Math.round(availabilityPct)}% available
            </Typography>
          </Box>
        </Box>

        {/* ── Two-column: Details + Borrow Records ── */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'stretch',
        }}>
          {/* Item Details */}
          <Section title="Item Details" icon={InfoIcon} accent={NEON} delay={0.06} bodyFlex>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <InfoRow icon={LocationIcon} label="Storage Location" value={stock.place}       color={NEON} />
              <InfoRow icon={BusinessIcon} label="Department"       value={department?.name}  color={NEON} />
            </Box>

            {/* Divider */}
            <Box sx={{ my: 1.5, borderTop: `1px solid ${alpha(NEON, 0.08)}` }} />

            {/* Remarks inside Item Details */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: '7px', flexShrink: 0,
                background: alpha(NEON, 0.1), border: `1px solid ${alpha(NEON, 0.2)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <DescriptionIcon sx={{ fontSize: 13, color: alpha(NEON, 0.8) }} />
              </Box>
              <Typography sx={{
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.62rem',
                color: TEXT_MUTED, textTransform: 'uppercase',
                letterSpacing: '0.07em', fontWeight: 600,
              }}>
                Remarks & Notes
              </Typography>
            </Box>
            <Box sx={{
              flex: 1,
              px: 1.75, py: 1.5,
              borderRadius: '0 9px 9px 0',
              borderLeft: `3px solid ${alpha(NEON, 0.35)}`,
              background: alpha(SURFACE_TOP, 0.4),
            }}>
              <Typography sx={{
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem',
                color: stock.remark ? TEXT_BODY : TEXT_MUTED,
                fontStyle: stock.remark ? 'normal' : 'italic',
                lineHeight: 1.75,
              }}>
                {stock.remark || 'No remarks provided.'}
              </Typography>
            </Box>
          </Section>

          {/* Borrow Records — matches Item Details height */}
          <Section
            title={`Borrow Records (${borrowRecords.length})`}
            icon={BorrowIcon}
            accent={NEON2}
            delay={0.09}
            bodyFlex
          >
            {borrowRecords.length === 0 ? (
              <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.87rem', color: TEXT_MUTED, fontStyle: 'italic',
              }}>
                No borrow records for this item yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {/* Scrollable list — fills remaining height */}
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  pr: 0.5,
                  /* Custom scrollbar */
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(NEON2, 0.25),
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': { background: alpha(NEON2, 0.45) },
                }}>
                  {borrowRecords.map((borrow) => (
                    <Box
                      key={borrow.id}
                      component="button"
                      type="button"
                      onClick={() => navigate(`/borrows/${borrow.id}`)}
                      sx={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', gap: 1,
                        width: '100%', textAlign: 'left',
                        px: 1.5, py: 1.1, border: 'none',
                        borderRadius: '10px',
                        outline: `1px solid ${alpha(NEON, 0.12)}`,
                        background: alpha(SURFACE_TOP, 0.35),
                        cursor: 'pointer', flexShrink: 0,
                        transition: 'outline-color 0.18s ease, background 0.18s ease',
                        '&:hover': {
                          outlineColor: alpha(NEON, 0.32),
                          background: alpha(NEON, 0.05),
                        },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.86rem', fontWeight: 600, color: TEXT_BODY,
                        }}>
                          {borrow.borrower_name}
                        </Typography>
                        <Typography sx={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.73rem', color: TEXT_MUTED, mt: 0.2,
                        }}>
                          Qty {borrow.quantity}
                          {borrow.penalty_date
                            ? ` · Due ${dayjs(borrow.penalty_date).format('D MMM YYYY')}`
                            : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        <BorrowBadge status={borrow.status} penaltyDate={borrow.penalty_date} archivedAt={borrow.archived_at} />
                        <ChevronRightIcon sx={{ fontSize: 15, color: alpha(NEON, 0.4) }} />
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Sticky footer link */}
                <Button
                  size="small"
                  onClick={goToBorrowList}
                  sx={{
                    alignSelf: 'flex-start', mt: 1, flexShrink: 0,
                    color: NEON, fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600, textTransform: 'none', fontSize: '0.82rem',
                    px: 0,
                    '&:hover': { background: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  View all in borrow list →
                </Button>
              </Box>
            )}
          </Section>
        </Box>

        {/* ── System Information ── */}
        <SystemInformationSection
          createdBy={formatAuditUser(stock.record_creator)}
          createdAt={dayjs(stock.created_at).format('DD MMM YYYY, h:mm a')}
          updatedBy={formatAuditUser(stock.last_updater)}
          updatedAt={dayjs(stock.updated_at).format('DD MMM YYYY, h:mm a')}
          delay={0.15}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      {/* ── Delete Confirmation ── */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteStock}
        itemType="stock item"
        itemName={stock?.things}
        title="Archive Inventory Item"
        description="The stock item will be archived, prevented from new borrows, and remain in the system for inventory history."
        confirmLabel="Archive Inventory Item"
        actionVerb="archive"
      />
    </Container>
  );
};

export default StockDetails;
